'use client';
/**
 * Google Analytics 4 Helper Functions
 * This file provides utility functions to interact with the GA4 tracking.
 * It encapsulates the logic for sending page views, generic events, and click events.
 */

// Declare gtag function for TypeScript to avoid 'gtag is not defined' errors
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event',
      targetId: string,
      params?: { [key: string]: any }
    ) => void;
  }
}

/**
 * Tracks a generic event with Google Analytics 4.
 *
 * @param {string} eventName - The name of the event (e.g., 'login', 'add_to_cart', 'page_load_more').
 * @param {object} [eventParams] - Optional parameters for the event.
 * Example: { category: 'engagement', action: 'scroll', label: 'infinite_scroll' }
 */
export const trackGA4Event = (eventName: string, eventParams?: { [key: string]: any }) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // console.log(`GA4: Tracking event - ${eventName}`, eventParams);
    window.gtag('event', eventName, eventParams);
  } else {
    console.warn('GA4: gtag function not available. Google Analytics script might not have loaded yet.');
  }
};

/**
 * Tracks a page view with Google Analytics 4.
 * Note: The @next/third-parties/google/GoogleAnalytics component typically handles
 * initial page views and client-side route changes automatically if Enhanced Measurement is enabled.
 * This function can be used for explicit page view tracking if needed.
 *
 * @param {string} url - The full URL of the page being viewed.
 * @param {string} title - The title of the page being viewed.
 */
export const trackGA4PageView = (url: string, title: string, additionalLabels?: { [key: string]: string | number | any }) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // console.log(`GA4: Tracking page view - URL: ${url}, Title: ${title}`);
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: title,
      ...additionalLabels // Merge any additional custom labels if needed
    });
  } else {
    console.warn('GA4: gtag function not available for page view. Google Analytics script might not have loaded yet.');
  }
};

/**
 * Tracks a click event with Google Analytics 4.
 * This is a specialized function that calls trackGA4Event with click-specific parameters.
 *
 * @param {string} elementId - The ID of the clicked element.
 * @param {string} [category='Interaction'] - The category of the click (e.g., 'Navigation', 'Form').
 * @param {string} [action='Click'] - The action performed (e.g., 'Click', 'Submit').
 * @param {string} [label] - An optional label for more detail (e.g., 'Main CTA').
 */
export const trackGA4Click = (
  elementId: string,
  category: string = 'Interaction',
  action: string = 'Click',
  label?: string,
  additionalParams?: { [key: string]: any }
) => {
  trackGA4Event('click_event', { // Using a custom event name for clicks
    event_category: category,
    event_action: action,
    event_label: label || elementId,
    element_id: elementId, // Custom parameter for element ID
    ...additionalParams
  });
};
